import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDefaultCoverImage as getStaticDefaultCover } from "@/lib/defaultCoverImages";

interface DbCoverImage {
  country_name: string;
  image_url: string;
  position: number;
}

/**
 * Simple hash function to get a consistent index from a string
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

/**
 * Hook that fetches default cover images from the database
 * and provides a resolver function.
 * Falls back to the static file if DB data isn't available yet.
 */
export const useDefaultCoverImages = () => {
  const { data: dbImages } = useQuery({
    queryKey: ["country-default-images-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("country_default_images")
        .select("country_name, image_url, position")
        .order("country_name, position");
      if (error) throw error;
      
      // Group by country
      const map: Record<string, string[]> = {};
      for (const img of (data as DbCoverImage[])) {
        if (!map[img.country_name]) map[img.country_name] = [];
        map[img.country_name].push(img.image_url);
      }
      return map;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });

  /**
   * Resolves a default cover image for the given countries.
   * Checks DB images first, then falls back to the static file.
   */
  const getDefaultCoverImage = (countries: string | string[], seed?: string): string => {
    const countryList = Array.isArray(countries) ? countries : countries.split(",").map(c => c.trim());

    // Try DB images first
    if (dbImages) {
      for (const country of countryList) {
        const images = dbImages[country];
        if (images && images.length > 0) {
          if (seed) {
            const index = hashString(seed) % images.length;
            return images[index];
          }
          return images[0];
        }
      }
    }

    // Fallback to static file
    return getStaticDefaultCover(countryList, seed);
  };

  return { getDefaultCoverImage };
};
